import {useEffect, useRef, useCallback} from 'react';

const INTERVAL_MS = 1000;

/**
 * Polls every 1s while `active` is true (typically while progress is 0).
 * Stops automatically when `active` becomes false.
 *
 * @param {Function} pollFn - Stable callback (use useCallback) that fetches progress.
 * @param {boolean} active - Whether polling should be running.
 * @returns {Function} cleanup - Call manually to stop polling (e.g., on cancel).
 */
export default function useZeroProgressPolling(pollFn, active) {
    const intervalRef = useRef(null);

    const clear = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!active) {
            clear();
            return;
        }

        pollFn();
        intervalRef.current = setInterval(pollFn, INTERVAL_MS);

        return clear;
    }, [active, pollFn, clear]);

    return clear;
}
